import { createSlice } from "@reduxjs/toolkit";
import {
	fetchCustomerAddresses, updateCustomerAddress, deleteCustomerAddress, fetchCustomerOrders,
	updateCustomerInfo, createCustomerAddress, updateCustomerDefaultAddress, getCustomerProfile,
	influenceDigest
} from "actions/account/profile";

import { CUSTOMER_STATE } from "utils/constants";
import { LOGOUT } from "utils/action-types";

const initialState = {
	displayName: "",
	firstName: "",
	lastName: "",
	emailAddress: {},
	phoneNumber: {},
	creationDate: "",
	addresses: {},
	addressPageInfo: {},
	defaultAddress: null,
	id: "",
	digest: null,
	customerState: null,
	isAuthenticated: false,
	hasOrders: false,
	orders: {
		loading: true,
		data: [],
		numberOfOrders: null,
		pageInfo: {
			admin: null,
			storefront: null,
		},
	},
	error: null,
	disabled: false,
	latestSubOrder: null,
	churnDiscount20: false,
	churnDiscount25: false,
};

const profileSlice = createSlice({
	name: "auth",
	initialState,
	reducers: {
		setChurnDiscount: (state, action) => {
			const { key, value } = action.payload;
			if (key === "discount_20") {
				state.churnDiscount20 = value;
			} else if (key === "discount_25") {
				state.churnDiscount25 = value;
			}
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(LOGOUT, () => initialState)
			.addCase(getCustomerProfile.fulfilled, (state, action) => {
				Object.assign(state, action.payload);
				state.customerState = action.payload.id ? CUSTOMER_STATE.Logged_in : CUSTOMER_STATE.Logged_out;
				state.isAuthenticated = !!action.payload.id;

				// Extract churn discount metafields
				const metafields = action.payload?.metafields || [];
				const churn20 = metafields.find(mf => mf?.namespace === "churn" && mf?.key === "discount_20");
				const churn25 = metafields.find(mf => mf?.namespace === "churn" && mf?.key === "discount_25");
				state.churnDiscount20 = churn20?.value === "true";
				state.churnDiscount25 = churn25?.value === "true";
			})
			.addCase(getCustomerProfile.rejected, (state) => {
				state.customerState = CUSTOMER_STATE.Logged_out;
				state.isAuthenticated = false;
			})
			.addCase(influenceDigest.fulfilled, (state, action) => {
				state.digest = action.payload;
			})
			.addCase(fetchCustomerAddresses.fulfilled, (state, action) => {
				const { addresses, pageInfo, defaultAddress } = action.payload;
				const cursor = action.meta.arg;
				if (cursor) {
					Object.assign(state.addresses, addresses);
				} else {
					state.addresses = addresses;
				}
				state.addressPageInfo = pageInfo;
				state.defaultAddress = defaultAddress;
			})
			.addCase(updateCustomerDefaultAddress.fulfilled, (state, action) => {
				const { address, id } = action.payload;
				state.defaultAddress = address;
				if (id) {
					state.addresses[id] = {
						...(state.addresses[id] || {}),
						...address,
					};
				}
			})
			.addCase(updateCustomerAddress.fulfilled, (state, action) => {
				const { address, id } = action.payload;
				if (action.meta.arg.defaultAddress) {
					state.defaultAddress = address;
				}
				state.addresses[id] = address;
			})
			.addCase(createCustomerAddress.fulfilled, (state, action) => {
				const { address, id } = action.payload;
				state.addresses[id] = address;
				if (action.meta.arg.defaultAddress) {
					state.defaultAddress = address;
				}
				state.addresses[id] = address;
			})
			.addCase(deleteCustomerAddress.fulfilled, (state, action) => {
				const { id } = action.payload;
				delete state.addresses[id];
				if (Object.keys(state.addresses).length === 0) {
					state.defaultAddress = null;
				}
			})
			.addCase(updateCustomerInfo.fulfilled, (state, action) => {
				Object.assign(state, action.payload.customer);
			})
			.addCase(fetchCustomerOrders.fulfilled, (state, action) => {
				const arg = action.meta.arg;
				if (arg && arg.admin?.endCursor) {
					state.orders.data = state.orders.data.concat(action.payload.orders);
				} else {
					state.orders.data = action.payload.orders;
				}
				state.orders.numberOfOrders = action.payload.numberOfOrders;
				state.orders.pageInfo = action.payload.pageInfo;
				state.orders.loading = false;

				const sortedSubOrders = state.orders.data
					.filter((o) => o.isSubscription)
					.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
				const latestSubOrder = sortedSubOrders[0];
				const mappedOrder = latestSubOrder
					? {
						fulfillments: latestSubOrder.fulfillments,
						createdAt: latestSubOrder.createdAt,
						cancelledAt: latestSubOrder.cancelledAt,
						deliveryDate: latestSubOrder.customAttributes?.find(
							(a) => a.key === "Lieferdatum"
						)?.value,
						totalQuantity:
							latestSubOrder.lineItems?.nodes?.reduce(
								(acc, item) => acc + item.quantity,
								0
							) ?? 0,
					}
					: null;
				state.latestSubOrder = mappedOrder;
			})
			.addCase(fetchCustomerOrders.rejected, (state) => {
				state.orders.loading = false;
			});
	}
});

export const { setChurnDiscount } = profileSlice.actions;
export default profileSlice.reducer;
